import * as React from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Pencil, Plus, Zap } from "lucide-react";
import { slugify } from "@poji/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckboxField, Input, Label, NativeSelect, Textarea } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { Mono, PageHeader } from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { date } from "@/lib/format";

// ---------------------------------------------------------------- API biçimi

type Tr = Record<
  string,
  { name: string; shortDescription?: string | null; description?: string | null; slug: string }
>;

export interface Market {
  id: string;
  code: string;
  name: string;
  defaultLocale: string;
  currency: string;
  isActive: boolean;
  locales: { code: string; name: string }[];
}
export interface Category {
  id: string;
  parentId: string | null;
  code: string;
  icon: string | null;
  sort: number | null;
  isSeasonal: boolean;
  isActive: boolean;
  serviceCount: number;
  translations: Tr;
}
export interface Service {
  id: string;
  code: string;
  formTemplateId: string;
  pricingType: "hourly" | "fixed";
  taxCategoryId: string | null;
  defaultMinMinutes: number | null;
  locationModes: "customer" | "provider" | "both";
  urgentEnabled: boolean;
  quotesEnabled: boolean;
  requiredLicense: string | null;
  requiresConduct: boolean;
  icon: string | null;
  isActive: boolean;
  updatedAt: string | null;
  categoryIds: string[];
  markets: { marketId: string; isActive: boolean }[];
  taskCount: number;
  providerCount: number;
  translations: Tr;
}
export interface CatalogData {
  markets: Market[];
  formTemplates: { id: string; code: string; kind: string; version: number; isActive: boolean }[];
  taxCategories: { id: string; marketId: string; code: string; name: string }[];
  categories: Category[];
  services: Service[];
}

export const useCatalog = () =>
  useQuery({
    queryKey: ["admin-catalog"],
    queryFn: () => api<CatalogData>("/v1/admin/catalog"),
  });

/** Panelde gösterilecek ad: önce İngilizce (pazar varsayılanı), yoksa ilk çeviri, yoksa kod */
export const nameOf = (x: { code: string; translations: Tr }) =>
  x.translations.en?.name ?? Object.values(x.translations)[0]?.name ?? x.code;

/** Tüm pazarların dilleri (sırayla, tekrarsız): en, mt, … */
const allLocales = (d: CatalogData) => {
  const seen = new Map<string, string>();
  for (const m of d.markets)
    for (const l of m.locales) if (!seen.has(l.code)) seen.set(l.code, l.name);
  return [...seen].map(([code, name]) => ({ code, name }));
};

const LOCATION: Record<Service["locationModes"], string> = {
  customer: "Müşterinin adresinde",
  provider: "Hizmet verenin yerinde",
  both: "İkisi de",
};
const toCode = (s: string) =>
  slugify(s)
    .replace(/-/g, "_")
    .replace(/^[^a-z]+/, "")
    .slice(0, 60);

// ---------------------------------------------------------------- çeviri alanları

type TrDraft = Record<string, { name: string; short: string; slug: string }>;
const trDraft = (tr: Tr, locales: { code: string }[], kind: "service" | "category"): TrDraft =>
  Object.fromEntries(
    locales.map((l) => [
      l.code,
      {
        name: tr[l.code]?.name ?? "",
        short: (kind === "service" ? tr[l.code]?.shortDescription : tr[l.code]?.description) ?? "",
        slug: tr[l.code]?.slug ?? "",
      },
    ]),
  );

function TranslationFields({
  locales,
  value,
  onChange,
  shortLabel,
  idPrefix,
}: {
  locales: { code: string; name: string }[];
  value: TrDraft;
  onChange: (v: TrDraft) => void;
  shortLabel: string;
  idPrefix: string;
}) {
  const set = (code: string, patch: Partial<TrDraft[string]>) =>
    onChange({ ...value, [code]: { ...value[code]!, ...patch } });
  return (
    <div className="grid gap-3">
      {locales.map((l, i) => {
        const v = value[l.code]!;
        return (
          <fieldset key={l.code} className="grid gap-2 rounded-lg border p-3">
            <legend className="px-1 text-xs font-medium text-muted-foreground">
              {l.name} ({l.code}){i === 0 ? " · zorunlu" : " · boş bırakılırsa İngilizcesi görünür"}
            </legend>
            <Label htmlFor={`${idPrefix}-${l.code}-name`}>Ad</Label>
            <Input
              id={`${idPrefix}-${l.code}-name`}
              value={v.name}
              maxLength={120}
              required={i === 0}
              onChange={(e) => set(l.code, { name: e.target.value })}
            />
            <Label htmlFor={`${idPrefix}-${l.code}-short`}>{shortLabel}</Label>
            <Textarea
              id={`${idPrefix}-${l.code}-short`}
              value={v.short}
              maxLength={300}
              className="min-h-14"
              onChange={(e) => set(l.code, { short: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Site adresi: /{l.code}/…/
              <span className="font-mono">{v.slug || slugify(v.name) || "…"}</span>
            </p>
          </fieldset>
        );
      })}
    </div>
  );
}

/** Dolu çeviriler; adres (slug) değişmez (kayıtlı olan korunur, Google'daki bağlantılar kırılmasın) */
const trBody = (d: TrDraft, kind: "service" | "category") =>
  Object.fromEntries(
    Object.entries(d)
      .filter(([, v]) => v.name.trim())
      .map(([code, v]) => [
        code,
        kind === "service"
          ? { name: v.name.trim(), shortDescription: v.short.trim() || null, slug: v.slug || null }
          : { name: v.name.trim(), description: v.short.trim() || null, slug: v.slug || null },
      ]),
  );

// ---------------------------------------------------------------- hizmet formu

function ServiceDialog({ data, service }: { data: CatalogData; service?: Service }) {
  const qc = useQueryClient();
  const canForms = useAuth().can("catalog.forms.manage");
  const locales = allLocales(data);
  const init = React.useCallback(
    () => ({
      code: service?.code ?? "",
      tr: trDraft(service?.translations ?? {}, locales, "service"),
      categoryIds: service?.categoryIds ?? [],
      formTemplateId:
        service?.formTemplateId ?? data.formTemplates.find((f) => f.code === "general")?.id ?? "",
      pricingType: service?.pricingType ?? "hourly",
      locationModes: service?.locationModes ?? "customer",
      taxCategoryId: service?.taxCategoryId ?? data.taxCategories[0]?.id ?? "",
      urgentEnabled: service?.urgentEnabled ?? false,
      quotesEnabled: service?.quotesEnabled ?? false,
      requiredLicense: service?.requiredLicense ?? "",
      requiresConduct: service?.requiresConduct ?? false,
      isActive: service?.isActive ?? true,
      markets: Object.fromEntries(
        data.markets.map((m) => [
          m.id,
          service ? (service.markets.find((x) => x.marketId === m.id)?.isActive ?? false) : true,
        ]),
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service, data],
  );
  const [f, setF] = React.useState(init);
  const set = (patch: Partial<ReturnType<typeof init>>) => setF((x) => ({ ...x, ...patch }));
  const en = f.tr[locales[0]?.code ?? "en"]?.name ?? "";
  const code = service ? service.code : f.code || toCode(en);

  const canSubmit = Boolean(en.trim() && f.categoryIds.length && f.formTemplateId && code);

  return (
    <ActionDialog
      wide
      onOpen={() => setF(init())}
      trigger={
        service ? (
          <Button size="sm" variant="outline">
            <Pencil /> Düzenle
          </Button>
        ) : (
          <Button size="sm">
            <Plus /> Yeni hizmet
          </Button>
        )
      }
      title={service ? `Hizmeti düzenle: ${nameOf(service)}` : "Yeni hizmet"}
      description={
        service
          ? `${service.taskCount} iş ve ${service.providerCount} hizmet veren bu hizmete bağlı. Değişiklik müşteri uygulamasına 1 dakika içinde yansır.`
          : "Kaydedince müşteri uygulamasında hemen görünür (seçtiğiniz ülkelerde ve hizmet açıksa)."
      }
      confirmLabel="Kaydet"
      canSubmit={canSubmit}
      success={service ? "Hizmet güncellendi." : "Hizmet eklendi."}
      onSubmit={async (key) => {
        const body = {
          categoryIds: f.categoryIds,
          formTemplateId: f.formTemplateId,
          pricingType: f.pricingType,
          locationModes: f.locationModes,
          taxCategoryId: f.taxCategoryId || null,
          urgentEnabled: f.urgentEnabled,
          quotesEnabled: f.quotesEnabled,
          requiredLicense: f.requiredLicense.trim() || null,
          requiresConduct: f.requiresConduct,
          isActive: f.isActive,
          markets: Object.entries(f.markets).map(([marketId, isActive]) => ({
            marketId,
            isActive,
          })),
          translations: trBody(f.tr, "service"),
        };
        if (service)
          await api(`/v1/admin/catalog/services/${service.id}`, { method: "PATCH", body });
        else
          await api("/v1/admin/catalog/services", {
            method: "POST",
            body: { code, ...body },
            idempotencyKey: key,
          });
        await qc.invalidateQueries({ queryKey: ["admin-catalog"] });
      }}
    >
      <div className="grid gap-5">
        <TranslationFields
          idPrefix="svc"
          locales={locales}
          value={f.tr}
          onChange={(tr) => set({ tr })}
          shortLabel="Kısa açıklama (listede görünür)"
        />

        {!service && (
          <div className="grid gap-2">
            <Label htmlFor="svc-code">Sistem kodu</Label>
            <Input
              id="svc-code"
              value={f.code}
              placeholder={toCode(en) || "ör. dog_walking"}
              onChange={(e) => set({ code: e.target.value.toLowerCase() })}
              pattern="[a-z][a-z0-9_]{1,62}"
            />
            <p className="text-xs text-muted-foreground">
              Yalnızca küçük harf, rakam ve alt çizgi. Sonradan değişmez; boş bırakırsanız addan
              üretilir.
            </p>
          </div>
        )}

        <div className="grid gap-2">
          <Label>Kategoriler (en az bir)</Label>
          <div className="grid grid-cols-1 gap-2 rounded-lg border p-3 sm:grid-cols-2">
            {data.categories.map((c) => (
              <CheckboxField
                key={c.id}
                label={
                  <>
                    {nameOf(c)}{" "}
                    {!c.isActive && <span className="text-xs text-muted-foreground">(kapalı)</span>}
                  </>
                }
                checked={f.categoryIds.includes(c.id)}
                onChange={(e) =>
                  set({
                    categoryIds: e.target.checked
                      ? [...f.categoryIds, c.id]
                      : f.categoryIds.filter((x) => x !== c.id),
                  })
                }
              />
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="svc-pricing">Fiyatlandırma</Label>
            <NativeSelect
              id="svc-pricing"
              value={f.pricingType}
              onChange={(e) => set({ pricingType: e.target.value as Service["pricingType"] })}
            >
              <option value="hourly">Saatlik (hizmet veren saat ücretini belirler)</option>
              <option value="fixed">Sabit fiyat</option>
            </NativeSelect>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="svc-where">Nerede yapılır</Label>
            <NativeSelect
              id="svc-where"
              value={f.locationModes}
              onChange={(e) => set({ locationModes: e.target.value as Service["locationModes"] })}
            >
              {Object.entries(LOCATION).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="svc-form">Müşteriye sorulan form</Label>
            <NativeSelect
              id="svc-form"
              value={f.formTemplateId}
              onChange={(e) => set({ formTemplateId: e.target.value })}
            >
              {data.formTemplates
                .filter((t) => t.isActive || t.id === f.formTemplateId)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.code} (sürüm {t.version})
                  </option>
                ))}
            </NativeSelect>
            {canForms && f.formTemplateId && (
              <Link
                to={`/forms/${f.formTemplateId}`}
                className="text-xs text-primary hover:underline"
              >
                Bu formun sorularını gör ve düzenle →
              </Link>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="svc-tax">KDV kategorisi</Label>
            <NativeSelect
              id="svc-tax"
              value={f.taxCategoryId}
              onChange={(e) => set({ taxCategoryId: e.target.value })}
            >
              {data.taxCategories.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="svc-license">Gerekli lisans (varsa)</Label>
            <Input
              id="svc-license"
              value={f.requiredLicense}
              placeholder="ör. electrical, gas — boşsa gerekmez"
              onChange={(e) => set({ requiredLicense: e.target.value.toLowerCase() })}
            />
          </div>
        </div>

        <div className="grid gap-2">
          <Label>Ülkeler</Label>
          <div className="grid gap-2 rounded-lg border p-3">
            {data.markets.map((m) => (
              <CheckboxField
                key={m.id}
                label={`${m.name} (${m.code})${m.isActive ? "" : " · pazar henüz açılmadı"}`}
                checked={f.markets[m.id] ?? false}
                onChange={(e) => set({ markets: { ...f.markets, [m.id]: e.target.checked } })}
              />
            ))}
          </div>
        </div>

        <div className="grid gap-3">
          <CheckboxField
            label="Acil çağrı açılabilir"
            hint="Müşteri “hemen gelsin” seçebilir; acil ücreti Ücret kuralları ekranından."
            checked={f.urgentEnabled}
            onChange={(e) => set({ urgentEnabled: e.target.checked })}
          />
          <CheckboxField
            label="Teklif istenebilir"
            hint="Fiyatı işe göre değişen hizmetlerde (boya, taşıma…) müşteri en çok 3 hizmet verenden toplam fiyat ister."
            checked={f.quotesEnabled}
            onChange={(e) => set({ quotesEnabled: e.target.checked })}
          />
          <CheckboxField
            label="Poji Verified şart"
            hint="Çocuklara hizmet veren işler (palyaço…): hizmet veren geçerli sabıka belgesi rozeti olmadan bu hizmeti açamaz ve aramada görünmez."
            checked={f.requiresConduct}
            onChange={(e) => set({ requiresConduct: e.target.checked })}
          />
          <CheckboxField
            label="Hizmet açık"
            hint="Kapatırsanız yeni iş alınmaz ve listeden kalkar; geçmiş işler ve faturalar etkilenmez."
            checked={f.isActive}
            onChange={(e) => set({ isActive: e.target.checked })}
          />
        </div>
      </div>
    </ActionDialog>
  );
}

// ---------------------------------------------------------------- kategori formu

function CategoryDialog({ data, category }: { data: CatalogData; category?: Category }) {
  const qc = useQueryClient();
  const locales = allLocales(data);
  const init = React.useCallback(
    () => ({
      code: category?.code ?? "",
      tr: trDraft(category?.translations ?? {}, locales, "category"),
      isActive: category?.isActive ?? true,
      sort: category?.sort === null || category?.sort === undefined ? "" : String(category.sort),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [category, data],
  );
  const [f, setF] = React.useState(init);
  const en = f.tr[locales[0]?.code ?? "en"]?.name ?? "";
  const code = category ? category.code : f.code || toCode(en);
  const sort = f.sort.trim() === "" ? null : Number(f.sort);
  return (
    <ActionDialog
      wide
      onOpen={() => setF(init())}
      trigger={
        category ? (
          <Button size="sm" variant="outline">
            <Pencil /> Düzenle
          </Button>
        ) : (
          <Button size="sm" variant="outline">
            <Plus /> Yeni kategori
          </Button>
        )
      }
      title={category ? `Kategoriyi düzenle: ${nameOf(category)}` : "Yeni kategori"}
      description={
        category && category.serviceCount
          ? `${category.serviceCount} hizmet bu kategoride. Kapatırsanız kategori müşteriye görünmez.`
          : "Kategori, içinde açık en az bir hizmet olunca müşteriye görünür."
      }
      confirmLabel="Kaydet"
      canSubmit={Boolean(en.trim() && code && (sort === null || Number.isInteger(sort)))}
      success={category ? "Kategori güncellendi." : "Kategori eklendi."}
      onSubmit={async (key) => {
        const body = { isActive: f.isActive, sort, translations: trBody(f.tr, "category") };
        if (category)
          await api(`/v1/admin/catalog/categories/${category.id}`, { method: "PATCH", body });
        else
          await api("/v1/admin/catalog/categories", {
            method: "POST",
            body: { code, ...body },
            idempotencyKey: key,
          });
        await qc.invalidateQueries({ queryKey: ["admin-catalog"] });
      }}
    >
      <div className="grid gap-5">
        <TranslationFields
          idPrefix="cat"
          locales={locales}
          value={f.tr}
          onChange={(tr) => setF((x) => ({ ...x, tr }))}
          shortLabel="Açıklama"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          {!category && (
            <div className="grid gap-2">
              <Label htmlFor="cat-code">Sistem kodu</Label>
              <Input
                id="cat-code"
                value={f.code}
                placeholder={toCode(en) || "ör. pet_care"}
                pattern="[a-z][a-z0-9_]{1,62}"
                onChange={(e) => setF((x) => ({ ...x, code: e.target.value.toLowerCase() }))}
              />
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="cat-sort">Sıra</Label>
            <Input
              id="cat-sort"
              inputMode="numeric"
              value={f.sort}
              placeholder="boşsa en sona"
              onChange={(e) => setF((x) => ({ ...x, sort: e.target.value }))}
            />
          </div>
        </div>
        <CheckboxField
          label="Kategori açık"
          checked={f.isActive}
          onChange={(e) => setF((x) => ({ ...x, isActive: e.target.checked }))}
        />
      </div>
    </ActionDialog>
  );
}

// ---------------------------------------------------------------- sayfa

export function CatalogPage() {
  const q = useCatalog();
  const { can } = useAuth();
  const [category, setCategory] = React.useState("");
  const [show, setShow] = React.useState<"all" | "active" | "inactive">("all");
  const [search, setSearch] = React.useState("");
  const data = q.data;
  const catName = React.useMemo(
    () => new Map((data?.categories ?? []).map((c) => [c.id, nameOf(c)])),
    [data],
  );

  const services = (data?.services ?? []).filter(
    (s) =>
      (!category || s.categoryIds.includes(category)) &&
      (show === "all" || (show === "active") === s.isActive) &&
      (!search.trim() ||
        `${nameOf(s)} ${s.code}`.toLowerCase().includes(search.trim().toLowerCase())),
  );

  const serviceColumns: ColumnDef<Service, unknown>[] = [
    {
      id: "name",
      header: "Hizmet",
      accessorFn: (s) => nameOf(s),
      cell: ({ row }) => (
        <div className="min-w-48">
          <div className="font-medium">{nameOf(row.original)}</div>
          <Mono>{row.original.code}</Mono>
        </div>
      ),
    },
    {
      id: "categories",
      header: "Kategori",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.categoryIds.map((id) => catName.get(id) ?? "?").join(", ")}
        </span>
      ),
    },
    {
      id: "pricing",
      header: "Fiyat",
      cell: ({ row }) => (row.original.pricingType === "hourly" ? "Saatlik" : "Sabit"),
    },
    {
      id: "markets",
      header: "Ülke",
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.markets.map((m) => {
            const mk = data?.markets.find((x) => x.id === m.marketId);
            return (
              <Badge key={m.marketId} variant={m.isActive ? "default" : "secondary"}>
                {mk?.code ?? "?"}
                {!m.isActive && " kapalı"}
              </Badge>
            );
          })}
          {row.original.urgentEnabled && (
            <Badge variant="warning" title="Acil çağrı açık">
              <Zap className="size-3" /> Acil
            </Badge>
          )}
          {row.original.quotesEnabled && (
            <Badge variant="secondary" title="Teklif istenebilir">
              Teklif
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: "usage",
      header: "Kullanım",
      accessorFn: (s) => s.taskCount,
      cell: ({ row }) => (
        <span className="tabular text-sm text-muted-foreground">
          {row.original.taskCount} iş · {row.original.providerCount} hizmet veren
        </span>
      ),
    },
    {
      id: "status",
      header: "Durum",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="success">Açık</Badge>
        ) : (
          <Badge variant="secondary">Kapalı</Badge>
        ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        data && (
          <div className="flex justify-end">
            <ServiceDialog data={data} service={row.original} />
          </div>
        ),
    },
  ];

  const categoryColumns: ColumnDef<Category, unknown>[] = [
    {
      id: "name",
      header: "Kategori",
      accessorFn: (c) => nameOf(c),
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{nameOf(row.original)}</div>
          <Mono>{row.original.code}</Mono>
        </div>
      ),
    },
    {
      id: "locales",
      header: "Diller",
      cell: ({ row }) => (
        <div className="flex gap-1">
          {Object.keys(row.original.translations).map((l) => (
            <Badge key={l} variant="outline">
              {l}
            </Badge>
          ))}
        </div>
      ),
    },
    { accessorKey: "serviceCount", header: "Hizmet sayısı" },
    { accessorKey: "sort", header: "Sıra" },
    {
      id: "status",
      header: "Durum",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="success">Açık</Badge>
        ) : (
          <Badge variant="secondary">Kapalı</Badge>
        ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        data &&
        can("catalog.categories.manage") && (
          <div className="flex justify-end">
            <CategoryDialog data={data} category={row.original} />
          </div>
        ),
    },
  ];

  const lastChange = data?.services
    .map((s) => s.updatedAt)
    .filter(Boolean)
    .sort()
    .at(-1);

  return (
    <>
      <PageHeader
        title="Hizmetler"
        description={
          <>
            Müşteri uygulamasında ve sitede görünen hizmet kataloğu. Silme yok: kapatılan hizmet
            listeden kalkar, geçmiş işler korunur.
            {lastChange && <> Son değişiklik: {date(lastChange)}.</>}
          </>
        }
        actions={
          data && (
            <>
              {can("catalog.categories.manage") && <CategoryDialog data={data} />}
              <ServiceDialog data={data} />
            </>
          )
        }
      />
      <Tabs defaultValue="services">
        <TabsList className="mb-4">
          <TabsTrigger value="services">Hizmetler ({data?.services.length ?? "…"})</TabsTrigger>
          <TabsTrigger value="categories">
            Kategoriler ({data?.categories.length ?? "…"})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="services">
          <div className="mb-3 flex flex-wrap gap-2">
            <Input
              aria-label="Ara"
              placeholder="Ad ya da kod ara"
              className="w-56"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <NativeSelect
              aria-label="Kategori"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">Tüm kategoriler</option>
              {data?.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {nameOf(c)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Durum"
              value={show}
              onChange={(e) => setShow(e.target.value as typeof show)}
            >
              <option value="all">Açık ve kapalı</option>
              <option value="active">Yalnızca açık</option>
              <option value="inactive">Yalnızca kapalı</option>
            </NativeSelect>
          </div>
          <DataTable
            columns={serviceColumns}
            data={data ? services : undefined}
            isLoading={q.isPending}
            error={q.error}
            onRetry={() => void q.refetch()}
            empty="Bu filtreye uyan hizmet yok."
          />
        </TabsContent>
        <TabsContent value="categories">
          <DataTable
            columns={categoryColumns}
            data={data?.categories}
            isLoading={q.isPending}
            error={q.error}
            onRetry={() => void q.refetch()}
            empty="Kategori yok."
          />
        </TabsContent>
      </Tabs>
    </>
  );
}
